import { 
    MenuItem, 
    Select,
    TextField,
    Button, 
    Paper,
    Typography,
    InputLabel,
    Chip } from "@material-ui/core";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import React from "react";
import intl from "react-intl-universal";
import { LicenseFilter, LicenseStatus, SpectralService } from "spectral-core/models/License";
import User from "spectral-core/models/User";

interface  LicenseFilterProps {
    filter: LicenseFilter;
    users: User[];
    onChangeFilter: React.Dispatch<React.SetStateAction<LicenseFilter>>;
    onApplyFilters: (filter: LicenseFilter) => void;
}

export default function LicenseFilterMenu({ 
    filter,
    users,
    onChangeFilter,
    onApplyFilters 
}: LicenseFilterProps) {

    const defaultFilter = {
        service: null,
        status: null,
        name: "",
        users: null
    };

    const handleEnumToggle = (field: "status" | "service", value: number | string) => {
        let enumValue = null;

        if (!isNaN(Number(value)))
            enumValue = value;

        onChangeFilter((prev) => ({
            ...prev,
            [field]: enumValue
        }));
    }

    const handleRemoveFilter = (field: string) => {
        onChangeFilter((prev) => ({
            ...prev,
            [field]: field === "status" || field === "service"
            ? null
            : "" 
        }));
    }

    const handleRemoveListFilter = (value: string) => {
        onChangeFilter((prev) => ({
            ...prev,
            ["users"]: filter.users?.filter(u => u !== value) 
                ?? null
        }));
    }

    return (
        <Paper style={{ padding: 16, marginBottom: 16 }}>

            <Typography variant="subtitle1" gutterBottom>
                {intl.get("license.filters")}
            </Typography>

            <GridContainer spacing={2} alignItems="center">
                {/*@ts-ignore*/}
                <GridItem xs={12} md={2}>
                    <InputLabel id="service-field-label">{intl.get("license.service")}</InputLabel>
                    <Select
                        labelId="service-field-label"
                        fullWidth
                        value={filter.service ?? intl.get("license.all")}
                        onChange={(e) => 
                            handleEnumToggle("service", e.target.value as number)
                        }>
                    <MenuItem key={-1} value={intl.get("license.all")}>{intl.get("license.all")}</MenuItem>
                    {Object.entries(SpectralService)
                    .filter(([key]) => isNaN(Number(key)))
                    .map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                            {key}
                        </MenuItem>
                    ))}
                    </Select>
                </GridItem>

                {/*@ts-ignore*/}
                <GridItem xs={12} md={2}> 
                    <InputLabel id="status-field-label">{intl.get("license.status")}</InputLabel>
                    <Select
                        fullWidth
                        labelId="status-field-label"
                        value={filter.status ?? intl.get("license.all")}
                        onChange={(e) => 
                            handleEnumToggle("status", e.target.value as number)
                        }>
                        <MenuItem key={-1} value={intl.get("license.all")}>{intl.get("license.all")}</MenuItem>
                        {Object.entries(LicenseStatus)
                        .filter(([key]) => isNaN(Number(key)))
                        .map(([key, value]) => (
                            <MenuItem key={value} value={value}>
                                {key}
                            </MenuItem>
                        ))}
                    </Select>
                </GridItem>
                
                {/*@ts-ignore*/}
                <GridItem xs={12} md={3}>
                    <TextField
                        label={intl.get("license.name")}
                        size="small"
                        fullWidth
                        value={filter.name}
                        onChange={(e) => 
                            onChangeFilter({...filter, name: e.target.value })
                        }/>

                </GridItem>
                {/*@ts-ignore*/}
                <GridItem xs={12} md={2}>
                    <InputLabel id="users-field-label">{intl.get("license.users")}</InputLabel>
                    <Select
                        labelId="users-field-label"
                        multiple
                        fullWidth
                        value={filter.users ?? []}
                        onChange={(e) => 
                            onChangeFilter({ ...filter, users: e.target.value as string[] })
                        }>
                        {users.map((user) => (
                            <MenuItem key={user.email} value={user.email}>
                                {user.email}
                            </MenuItem>
                        ))}  
                    </Select>
                </GridItem>
                {/*@ts-ignore*/}
                <GridItem xs={12} md={3} style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        fullWidth
                        style={{ backgroundColor: "#00acc1", color: "#fff" }}
                        onClick={() => onApplyFilters(filter)}>
                            {intl.get("license.apply")}
                    </Button>

                    <Button
                        variant="text"
                        fullWidth
                        onClick={(e) => 
                            onChangeFilter(defaultFilter)
                        }>
                            {intl.get("license.clean")}
                    </Button>
                </GridItem>
            </GridContainer>

            <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {filter.service !== null && (
                <Chip 
                    label={`${intl.get("license.service")}: ${SpectralService[filter.service]}`}
                    onDelete={() => handleRemoveFilter("service")}
                />
                )}

                {filter.status != null && (
                <Chip 
                    label={`${intl.get("license.status")}: ${LicenseStatus[filter.status]}`}
                    onDelete={() => handleRemoveFilter("status")}
                />
                )}

                {filter.name && (
                <Chip 
                    label={`${intl.get("license.name")}: ${filter.name}`}
                    onDelete={() => handleRemoveFilter("name")}
                />
                )}

                {filter.users && 
                filter.users.map((user) => 
                    <Chip 
                        label={`${intl.get("license.user")}: ${user}`}
                        onDelete={() => handleRemoveListFilter(user)}
                    />
                )}
            </div>
        </Paper>
    );
}